import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import PublicHeader from '../components/layout/PublicHeader';
import { SEO_BASE_URL } from '../constants/seo';
import { FEATURE_SEO_DATA, type FeatureSlug } from '../data/featureSEOData';

const ABOUT_FEATURES: FeatureSlug[] = ['pomodoro-timer', 'deep-work', 'focus-sounds', 'task-manager', 'productivity-tools'];

const ORGANIZATION_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Focuzon',
  url: SEO_BASE_URL,
  description:
    'Focuzon is a productivity platform that helps users focus using Pomodoro timers, deep work sessions and task management tools.',
  contactPoint: {
    '@type': 'ContactPoint',
    email: 'support@focuzon.com',
    contactType: 'customer support',
  },
};

const AboutPage: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, []);

  const goToDashboard = () => {
    navigate('/dashboard');
  };

  return (
    <>
      <Helmet>
        <html lang="en" />
        <title>About Focuzon | Deep Work &amp; Productivity Platform</title>
        <meta
          name="description"
          content="Learn about Focuzon, a productivity platform designed to help people focus using deep work sessions, Pomodoro timers and task management tools."
        />
        <meta name="keywords" content="about focuzon, focus app about, deep work app, productivity app, Pomodoro timer, task management, focus tool" />
        <link rel="canonical" href={`${SEO_BASE_URL}/about`} />
        <meta property="og:title" content="About Focuzon | Deep Work &amp; Productivity Platform" />
        <meta
          property="og:description"
          content="Learn about Focuzon, a productivity platform designed to help people focus using deep work sessions, Pomodoro timers and task management tools."
        />
        <meta property="og:url" content={`${SEO_BASE_URL}/about`} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="About Focuzon | Deep Work &amp; Productivity Platform" />
        <meta
          name="twitter:description"
          content="Learn about Focuzon, a productivity platform for deep work, Pomodoro timers and task management."
        />
        <script type="application/ld+json">{JSON.stringify(ORGANIZATION_SCHEMA)}</script>
      </Helmet>
      <div className="min-h-screen bg-[#FFF8F0] relative overflow-hidden">
        {/* Background gradient shapes */}
        <div className="fixed inset-0 pointer-events-none z-0">
          <div className="absolute top-0 -left-1/4 w-96 h-96 bg-gradient-to-br from-orange-300/50 to-orange-200/40 rounded-full blur-3xl"></div>
          <div className="absolute top-1/4 -right-1/4 w-96 h-96 bg-gradient-to-br from-orange-400/50 to-orange-300/40 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-1/4 w-80 h-80 bg-gradient-to-br from-orange-200/40 to-orange-100/30 rounded-full blur-3xl"></div>
        </div>

        <PublicHeader />

        {/* Content - Add padding-top for fixed header */}
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-12 relative z-10">
          <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 md:p-12 border border-orange-200/50 shadow-lg">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              About Focuzon
            </h1>

            <div className="space-y-8 text-gray-700 leading-relaxed">
              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">What is Focuzon</h2>
                <p>
                  Focuzon is a productivity platform designed to help people focus and complete meaningful work. Explore our core focus techniques below.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">Our Focus Techniques</h2>
                <p className="mb-6">Learn about the focus techniques we use to power Focuzon. Click any technique to read more about what it is, its benefits, and how to use it.</p>
                <div className="space-y-4">
                  {ABOUT_FEATURES.map((slug) => {
                    const data = FEATURE_SEO_DATA[slug];
                    if (!data) return null;
                    const firstSection = data.h2Sections[0];
                    const summary = firstSection ? firstSection.content.split('. ').slice(0, 2).join('. ') + '.' : data.intro;
                    return (
                      <article
                        key={slug}
                        className="bg-orange-50/50 rounded-xl p-5 border border-orange-200/50 hover:border-orange-300/60 transition-colors"
                      >
                        <Link
                          to={`/${slug}`}
                          className="text-xl font-semibold text-orange-600 hover:text-orange-700 underline block mb-2"
                        >
                          {firstSection?.heading ?? data.h1}
                        </Link>
                        <p className="text-gray-600 text-sm mb-3">{summary}</p>
                        <Link
                          to={`/${slug}`}
                          className="inline-flex items-center text-orange-600 hover:text-orange-700 font-medium text-sm"
                        >
                          Learn more about {data.h1}
                          <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </Link>
                      </article>
                    );
                  })}
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">Productivity Philosophy</h2>
                <p>
                  Focuzon is built around proven productivity concepts. <strong>Deep work</strong>, uninterrupted focus on cognitively demanding tasks, lies at the core. We support <strong>focused work sessions</strong> with timers that create clear boundaries between work and breaks. The platform encourages <strong>distraction-free work</strong> through a calm interface and minimal notifications. Our approach to <strong>task planning systems</strong> helps you prioritize and execute without overwhelm. We believe focus isn't about working longer; it's about creating the right conditions for quality work.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">Why Focuzon Was Built</h2>
                <p>
                  We created Focuzon to solve a real problem: productivity apps that promise results but deliver noise. While others rely on endless notifications, gamified streaks, and guilt-inducing metrics, we built something different: a tool that actually helps you focus. Focuzon gives you a quiet space to work: set a timer, pick your focus sounds (or silence), and get things done. Our mission is to help people focus and complete meaningful work without distraction. We stay out of your way: no pop-ups, no motivational messages, just a clean workspace when you need it. Focus isn't about working longer hours; it's about finding those stretches where you're fully present and doing your best work.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">A ZFLOC Product</h2>
                <div className="bg-orange-50/50 rounded-xl p-6 border border-orange-200/50">
                  <p className="mb-3">
                    Focuzon is a product of <a href="https://www.zfloc.com/" target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:text-orange-700 font-medium underline">ZFLOC Technologies</a>, a company that builds exceptional web and mobile applications. Our team brings years of expertise in creating tools that people actually want to use.
                  </p>
                  <a
                    href="https://www.zfloc.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-orange-600 hover:text-orange-700 font-medium transition-colors text-sm"
                  >
                    Learn more about ZFLOC
                    <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                </div>
              </section>

              <section className="bg-gradient-to-br from-orange-100/60 to-orange-50/50 rounded-2xl p-8 border border-orange-200/50">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">Ready to Focus?</h2>
                <p className="mb-6">
                  Start a focus session and put the techniques you learned into practice. Set your timer, choose your task, and get into the zone. Sign up to get started.
                </p>
                <button
                  onClick={goToDashboard}
                  className="px-6 py-3 bg-orange-600 text-white rounded-xl font-medium hover:bg-orange-700 transition-colors"
                >
                  Start Focus Session
                </button>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">Get in Touch</h2>
                <p>
                  Have feedback, questions, or ideas? We'd love to hear from you. Our team reads every message and responds personally.
                </p>
                <div className="bg-orange-50/50 rounded-xl p-6 border border-orange-200/50 mt-4">
                  <a
                    href="mailto:support@focuzon.com"
                    className="text-orange-600 hover:text-orange-700 font-medium text-lg transition-colors"
                  >
                    support@focuzon.com
                  </a>
                  <p className="text-sm text-gray-600 mt-2">
                    Every message is read by our team. No bots, no templates. Just genuine, thoughtful responses.
                  </p>
                </div>
              </section>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default AboutPage;
